import z from 'zod'

import { SubscriberIdSchema } from '../subscriber/subscriber.dto'

/**
 * __UserId__
 *
 * The unique ID of a user in our system.
 */
export const UserIdSchema = z.string().brand('UserId')
export type UserId = z.infer<typeof UserIdSchema>

/**
 * __MergeUserId__
 *
 * If the user was retrieved from a third-party system via a Merge integration, this is Merge.dev's ID for the User.
 */
export const MergeUserIdSchema = z.string().brand('MergeUserId')
export type MergeUserId = z.infer<typeof MergeUserIdSchema>

/**
 * __RemoteUserId__
 *
 * If the user was retrieved from a third-party system, this is the remote ID that the third-party system uses to identify the user.
 */
export const RemoteUserIdSchema = z
  .union([z.string(), z.number()])
  .transform(String)
  .brand('RemoteUserId')
export type RemoteUserId = z.infer<typeof RemoteUserIdSchema>

/**
 * __REQUEST_USER__
 *
 * A constant used to inject the corresponding User object into a service or model when a human user is performing an action in our system.
 */
export const REQUEST_USER: unique symbol = Symbol.for('REQUEST_USER')

/**
 * __User__
 *
 * A user is a person who works for a subscriber.
 */
export const UserSchema = z.object({
  // The unique ID of the user.
  id: UserIdSchema,
  // Merge.dev's ID for the User if retrieved via a Merge integration.
  mergeUserId: MergeUserIdSchema.nullish().transform((val) => val ?? undefined),
  // The remote ID that the third-party system uses to identify the user.
  remoteId: RemoteUserIdSchema.nullish().transform((val) => val ?? undefined), // The ID of the subscriber to whom the user belongs.
  subscriberId: SubscriberIdSchema,
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  /**
   * __phoneNumber__
   *
   * The phone number of the user.
   */
  phoneNumber: z.string().nullable().optional(),
  updatedAt: z.date().optional(),
})

export type User = z.infer<typeof UserSchema>
